import React from 'react';
import { api } from '../lib/api';
import { fmtDate } from '../lib/format';
import { useResource } from '../lib/hooks';
import type { Billing as BillingInfo, BillingInvoice, PayTo } from '../lib/types';
import { Failed, Loading, Stat, type Ctx } from '../main';

/** Invoices show fils: AED 409.50, never AED 409.5. */
const aed = (n: number): string =>
  `AED ${n.toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const today = (): string => new Date().toISOString().slice(0, 10);
const daysUntil = (iso: string): number =>
  Math.round((Date.parse(`${iso}T00:00:00Z`) - Date.parse(`${today()}T00:00:00Z`)) / 86_400_000);

const STATUS_TONE: Record<BillingInvoice['status'], string> = { Open: 't-warn', Overdue: 't-bad', Paid: 't-ok', Void: 't-mute' };

/**
 * What the company owes BurjManage: its plan, its usage, its invoices, and the
 * dates on which an unpaid one makes the console read-only and then suspends
 * the account. Managers and admins only, like the rest of the money.
 */
export function BillingScreen({ ctx }: { ctx: Ctx }): React.JSX.Element {
  const { data, loading, error, reload } = useResource(() => api.billing(), []);
  if (loading) return <Loading />;
  if (error || !data) return <Failed error={error ?? new Error('No billing information')} onRetry={reload} />;
  const { plan, usage, invoices } = data;

  return (
    <>
      <Standing billing={data} large />

      {plan && (
        <div className="grid g4" style={{ margin: '16px 0' }}>
          <Stat label="Plan" value={plan.code[0]!.toUpperCase() + plan.code.slice(1)} foot={`${aed(plan.baseFee)} a month, ex. VAT`} />
          <Stat label="Rooms" value={`${usage.rooms} / ${plan.roomsAllowance}`}
            foot={usage.rooms > plan.roomsAllowance
              ? `${usage.rooms - plan.roomsAllowance} beyond the allowance, at ${aed(plan.perRoomFee)} each`
              : 'Within the allowance'} />
          <Stat label="Seats" value={`${usage.seats} / ${plan.seatsLimit > 900 ? '∞' : plan.seatsLimit}`} />
          <Stat label="Properties" value={`${usage.properties} / ${plan.propertiesLimit > 900 ? '∞' : plan.propertiesLimit}`} />
        </div>
      )}

      <div className="grid" style={{ gridTemplateColumns: 'minmax(0,2fr) minmax(0,1fr)', alignItems: 'start', gap: 14 }}>
        <div className="card">
          <div className="hd"><h3>Invoices</h3></div>
          <table className="responsive">
            <thead><tr><th>Invoice</th><th>Month</th><th>Total</th><th>Due</th><th>Status</th><th /></tr></thead>
            <tbody>
              {invoices.map((i) => (
                <tr key={i.id}>
                  <td data-label="Invoice" className="mono">{i.number}</td>
                  <td data-label="Month">{monthName(i.period)}</td>
                  <td data-label="Total" className="mono">{aed(i.total)}</td>
                  <td data-label="Due" className="mono">{fmtDate(i.dueOn)}</td>
                  <td data-label="Status">
                    <span className={`tag ${STATUS_TONE[i.status]}`}>{i.status}</span>
                    {i.paidOn && <div className="sub">paid {fmtDate(i.paidOn)}</div>}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="btn sm" onClick={() => printInvoice(i, data)}>View / print</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {invoices.length === 0 && (
            <div className="empty"><b>No invoices yet</b>The first is issued on the 1st of next month.</div>
          )}
        </div>
        <HowToPay payTo={data.payTo} />
      </div>
      {!ctx.isAdmin && (
        <div className="note" style={{ marginTop: 14 }}>
          Invoices and reminders are emailed to your company&apos;s admins.
        </div>
      )}
    </>
  );
}

/** Where to send the money, and the one thing to put on the transfer. */
function HowToPay({ payTo }: { payTo: PayTo }): React.JSX.Element {
  return (
    <div className="card"><div className="bd">
      <h3 style={{ fontSize: 14, marginBottom: 10 }}>How to pay</h3>
      {payTo.iban ? (
        <dl className="kv">
          <dt>Bank transfer to</dt><dd>{payTo.accountName || payTo.legalName}</dd>
          {payTo.bankName && <><dt>Bank</dt><dd>{payTo.bankName}</dd></>}
          <dt>IBAN</dt><dd className="mono">{payTo.iban}</dd>
          {payTo.swift && <><dt>SWIFT</dt><dd className="mono">{payTo.swift}</dd></>}
          <dt>Reference</dt><dd>the invoice number</dd>
        </dl>
      ) : (
        <div className="sub">BurjManage will send payment details with your invoice.</div>
      )}
      {payTo.email && (
        <div className="sub" style={{ marginTop: 10 }}>
          Once paid, send the transfer confirmation to <b>{payTo.email}</b> and it is marked straight away.
        </div>
      )}
    </div></div>
  );
}

/**
 * Where the account stands, in one sentence, with the dates. Shown on the
 * Billing screen and, while something is owed, across the top of every screen.
 */
export function Standing({ billing, large, onOpen }: {
  billing: BillingInfo; large?: boolean; onOpen?: () => void;
}): React.JSX.Element | null {
  const s = billing.standing;
  const unpaid = billing.invoices.filter((i) => i.status === 'Open' || i.status === 'Overdue');
  // on a free trial: when it ends, and what it then costs — across the top in its last week
  const trial = billing.org.status === 'Trialing' ? billing.org.trialEndsOn : null;
  if (trial && !unpaid.length) {
    const left = daysUntil(trial);
    if (!large && left > 7) return null;
    const p = billing.plan;
    return (
      <div className={large ? 'note' : 'banner warn'}>
        <b>Free trial {left <= 0 ? 'ends today' : `until ${fmtDate(trial)} (${left} day${left === 1 ? '' : 's'} left)`}.</b>
        {p && <> Then the {p.code} plan: {aed(p.baseFee)} a month plus VAT, {p.roomsAllowance} rooms included,
          and {aed(p.perRoomFee)} for each room beyond. The first invoice covers only the days after the trial.</>}
        {onOpen && <> <button className="linkish" onClick={onOpen}>Plan and billing</button></>}
      </div>
    );
  }
  if (!unpaid.length) {
    return large ? <div className="note ok"><b>Nothing owed.</b> Every invoice is paid. Thank you.</div> : null;
  }
  const open = onOpen ? <> <button className="linkish" onClick={onOpen}>See the invoice and how to pay</button></> : null;

  if (s.status === 'Suspended') {
    return (
      <div className={large ? 'note bad' : 'banner support'}>
        <b>This account is suspended</b> for an unpaid invoice of {aed(s.overdue || s.owed)}. Your team cannot work in
        BurjManage until it is paid; your residents can still use the tenant app.{open}
      </div>
    );
  }
  if (s.status === 'PastDue') {
    return (
      <div className={large ? 'note bad' : 'banner support'}>
        <b>Read-only:</b> {aed(s.overdue)} is overdue, so nothing can be changed until it is paid.
        {s.suspendOn && <> The account will be <b>suspended on {fmtDate(s.suspendOn)}</b>.</>}{open}
      </div>
    );
  }
  if (s.overdue > 0 && s.readOnlyOn && s.suspendOn) {
    const left = daysUntil(s.readOnlyOn);
    return (
      <div className={large ? 'note warn' : 'banner warn'}>
        <b>{aed(s.overdue)} is overdue.</b> If it is not paid, this console becomes read-only on{' '}
        <b>{fmtDate(s.readOnlyOn)}</b>{left > 0 ? ` (in ${left} day${left === 1 ? '' : 's'})` : ''} and the account is
        suspended on {fmtDate(s.suspendOn)}.{open}
      </div>
    );
  }
  // open, not yet due: worth a line on the Billing screen, and across the top in its last three days
  const due = s.oldestDueOn ? daysUntil(s.oldestDueOn) : 99;
  if (!large && due > 3) return null;
  return (
    <div className={large ? 'note' : 'banner warn'}>
      <b>{aed(s.owed)} is due{s.oldestDueOn ? ` on ${fmtDate(s.oldestDueOn)}` : ''}.</b>{open}
    </div>
  );
}

/** The whole page a suspended account sees: what is owed and how to pay, instead of an error. */
export function Suspended({ canSee, onSignOut }: { canSee: boolean; onSignOut: () => void }): React.JSX.Element {
  const billing = useResource(() => (canSee ? api.billing() : Promise.resolve(null)), [canSee]);
  return (
    <div className="gate"><div className="gatebox" style={{ maxWidth: 560 }}>
      {billing.data ? <Standing billing={billing.data} large /> : (
        <div className="note bad">
          <b>This account is suspended.</b> Your residents can still use the tenant app. Ask your company&apos;s
          admin, or BurjManage, to restore access.
        </div>
      )}
      {billing.data && (
        <>
          {billing.data.invoices.filter((i) => i.status === 'Overdue' || i.status === 'Open').map((i) => (
            <div className="rowcard" key={i.id} style={{ marginTop: 10 }}>
              <span className="body"><span className="tt">{i.number}</span>
                <span className="sub">{monthName(i.period)} · due {fmtDate(i.dueOn)} · {aed(i.total)}</span></span>
              <button className="btn sm" onClick={() => printInvoice(i, billing.data!)}>View / print</button>
            </div>
          ))}
          <div style={{ marginTop: 12 }}><HowToPay payTo={billing.data.payTo} /></div>
        </>
      )}
      <button className="btn full" style={{ marginTop: 12 }} onClick={onSignOut}>Sign out</button>
    </div></div>
  );
}

const monthName = (period: string): string =>
  new Date(`${period}-01T00:00:00Z`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });

const esc = (s: string): string =>
  s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));

/**
 * A tax invoice in a window of its own, for the browser's Print — and "Save as
 * PDF" — to take. Its own page rather than the console's print styles, so what
 * prints is the invoice and nothing else.
 */
export function printInvoice(inv: BillingInvoice, billing: BillingInfo): void {
  const p = billing.payTo;
  const rows = [
    [`BurjManage subscription, ${monthName(inv.period)}${billing.plan ? ` · ${billing.plan.code} plan` : ''}`
      + ` · peak ${inv.peakRooms} room${inv.peakRooms === 1 ? '' : 's'}`, aed(inv.subtotal)],
  ];
  const html = `<!doctype html><html lang="en"><head><meta charset="utf-8"><title>${esc(inv.number)}</title>
<style>
  body{font:13px/1.5 system-ui,-apple-system,"Segoe UI",sans-serif;color:#0B1D26;margin:40px;max-width:760px}
  h1{font-size:22px;margin:0 0 4px;letter-spacing:.04em} .muted{color:#5b6b70} table{width:100%;border-collapse:collapse;margin:22px 0}
  th,td{text-align:left;padding:9px 6px;border-bottom:1px solid #e3e8e6} td.n,th.n{text-align:right;font-variant-numeric:tabular-nums}
  .top{display:flex;justify-content:space-between;gap:30px} .tot td{font-weight:700;border-bottom:0}
  .paid{display:inline-block;border:2px solid #0F6B5C;color:#0F6B5C;padding:2px 10px;font-weight:800;letter-spacing:.1em;transform:rotate(-4deg)}
  .box{background:#f4f6f5;padding:12px 14px;border-radius:6px} @media print{body{margin:16mm}}
</style></head><body>
<div class="top">
  <div><h1>TAX INVOICE</h1><div class="muted">${esc(inv.number)}</div></div>
  <div style="text-align:right"><b>${esc(p.legalName)}</b><br>${esc(p.address).replace(/\n/g, '<br>')}
    ${p.trn ? `<br>TRN ${esc(p.trn)}` : ''}${p.email ? `<br>${esc(p.email)}` : ''}</div>
</div>
<div class="top" style="margin-top:26px">
  <div><div class="muted">Billed to</div><b>${esc(billing.org.name)}</b></div>
  <div style="text-align:right"><div>Issued ${esc(fmtDate(inv.issuedOn))}</div><div>Due ${esc(fmtDate(inv.dueOn))}</div>
    ${inv.status === 'Paid' ? `<div style="margin-top:8px"><span class="paid">PAID${inv.paidOn ? ` ${esc(fmtDate(inv.paidOn))}` : ''}</span></div>` : ''}</div>
</div>
<table><thead><tr><th>Description</th><th class="n">Amount</th></tr></thead><tbody>
${rows.map(([d, a]) => `<tr><td>${esc(d!)}</td><td class="n">${esc(a!)}</td></tr>`).join('')}
<tr><td class="muted">Subtotal</td><td class="n">${esc(aed(inv.subtotal))}</td></tr>
<tr><td class="muted">VAT 5%</td><td class="n">${esc(aed(inv.vat))}</td></tr>
<tr class="tot"><td>Total</td><td class="n">${esc(aed(inv.total))}</td></tr>
</tbody></table>
${inv.status === 'Paid' ? '' : p.iban ? `<div class="box"><b>Pay by bank transfer</b><br>
  ${esc(p.accountName || p.legalName)}${p.bankName ? ` · ${esc(p.bankName)}` : ''}<br>IBAN ${esc(p.iban)}${p.swift ? ` · SWIFT ${esc(p.swift)}` : ''}<br>
  Reference: <b>${esc(inv.number)}</b></div>` : ''}
<p class="muted" style="margin-top:22px">Amounts in UAE dirhams.</p>
<script>window.onload=function(){window.print()}</script>
</body></html>`;
  const w = window.open('', '_blank');
  if (!w) { alert('Allow pop-ups for this site to print the invoice.'); return; }
  w.document.write(html);
  w.document.close();
}
